import React, { useContext, useEffect, useRef } from 'react';
import {
  Box,
  SxProps,
  TextField,
  TextFieldProps,
  TypographyPropsVariantOverrides,
  useTheme,
} from '@mui/material';
import type { Variant } from '@mui/material/styles/createTypography';
import { borderRadiusValues } from 'shared/lib/utils/uiConstants';
import { UiContext } from 'shared/lib/contexts/uiContext';
import Typography from '@mui/material/Typography';
import { useCurrentBreakpoint } from 'shared/hooks/useCurrentBreakpoint';
import { useTypographyFonts } from 'shared/hooks/useGetTypographyFonts';
import { ClearTextInputAdornment } from 'shared/lib/components/controls/textFields/inputAdornments/clearTextInputAdornment';

export type CustomTextFieldProps = TextFieldProps & {
  typographyVariantSize?: keyof TypographyPropsVariantOverrides | Variant;
  labelSxProps?: SxProps;
  labelTypographyVariantSize?: keyof TypographyPropsVariantOverrides | Variant;
  wrapperSxProps?: SxProps;
  isClearIcon?: boolean;
};

export default function CustomTextField({
  label,
  typographyVariantSize,
  labelSxProps,
  labelTypographyVariantSize,
  wrapperSxProps,
  onChange,
  isClearIcon = false,
  ...props
}: CustomTextFieldProps) {
  const currentBp = useCurrentBreakpoint();
  const theme = useTheme();
  const {
    spacings: { sp },
  } = useContext(UiContext);
  const { getFontSize } = useTypographyFonts({ currentBp });

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (props.autoFocus) {
      inputRef.current?.focus();
    }
  }, [props.autoFocus]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: sp.s20,
        ...wrapperSxProps,
      }}
    >
      {label ? (
        <Typography
          sx={{
            ...(labelSxProps as SxProps<{}>),
            color: props.disabled
              ? 'text.disabled'
              : (labelSxProps as any)?.color || 'text.primary',
          }}
          variant={labelTypographyVariantSize || 'f16'}
        >
          {label}
        </Typography>
      ) : undefined}
      <TextField
        className="click-outside-exception"
        type="text"
        autoComplete="off"
        inputRef={inputRef}
        onChange={onChange}
        {...props}
        InputProps={{
          endAdornment:
            isClearIcon && props.value !== '' ? (
              <ClearTextInputAdornment
                position="end"
                onClick={() => {
                  if (inputRef.current) {
                    const nativeInputValueSetter =
                      Object.getOwnPropertyDescriptor(
                        window.HTMLInputElement.prototype,
                        'value'
                      )?.set;
                    nativeInputValueSetter?.call(inputRef.current, '');
                    const event = new Event('input', { bubbles: true });
                    inputRef.current.dispatchEvent(event);
                  }
                }}
              ></ClearTextInputAdornment>
            ) : undefined,
          ...props.InputProps,
        }}
        sx={{
          ...props.sx,
          '& textarea.MuiInputBase-input': {
            padding: 0,
            // @ts-ignore
            ...props?.sx?.['& textarea.MuiInputBase-input'],
          },
          '& .MuiInputBase-input.MuiInputBase-inputSizeSmall': {
            p: sp.s20,
            // @ts-ignore
            ...props?.sx?.['& .MuiInputBase-input.MuiInputBase-inputSizeSmall'],
          },
          '& .MuiInputBase-input': {
            p: 0,
            fontSize: typographyVariantSize
              ? getFontSize(typographyVariantSize)
              : undefined,
            borderRadius: '0px !important',
            // @ts-ignore
            ...props?.sx?.['& .MuiInputBase-input'],
          },
          '& .MuiInputBase-root': {
            py: sp.s30,
            px: sp.s50,
            // @ts-ignore
            ...props?.sx?.['& .MuiInputBase-root'],
          },
          '& .MuiOutlinedInput-root': {
            // @ts-ignore
            ...props?.sx?.['& .MuiOutlinedInput-root'],

            '& fieldset': {
              border: `1px solid ${theme.palette.custom.borders} !important`,
              // @ts-ignore
              ...props?.sx?.['& .MuiOutlinedInput-root']?.['& fieldset'],
            },
            '&.Mui-error fieldset': {
              border: `1px solid ${theme.palette.error.main} !important`,
            },
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderRadius: borderRadiusValues.small,
            // @ts-ignore
            ...props?.sx?.['& .MuiOutlinedInput-notchedOutline'],
          },
        }}
      ></TextField>
    </Box>
  );
}
